import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from "@mui/material";

import { cancelSubscription } from "../../../api";
import { logError } from "../../../logging/loggingService";
import AppDatePicker from "../../shared/AppDatePicker";

export default function CancelSubscriptionDialog({
    open,
    onClose,
    paidUntilDisplay,
    initialCancelDate,
    onRefreshUser,
    onNotify,
}) {
    const [cancelReason, setCancelReason] = useState("");
    const [cancelDate, setCancelDate] = useState("");
    const [cancelSubmitting, setCancelSubmitting] = useState(false);

    const todayIso = useMemo(() => new Date().toISOString().split("T")[0], []);

    useEffect(() => {
        if (!open) return;
        setCancelReason("");
        setCancelDate(initialCancelDate || "");
    }, [open, initialCancelDate]);

    const handleClose = () => {
        if (cancelSubmitting) return;
        onClose();
        setCancelReason("");
        setCancelDate("");
    };

    const handleCancelSubscription = async () => {
        setCancelSubmitting(true);
        try {
            const payload = {
                reason: cancelReason?.trim() ? cancelReason.trim() : null,
                requestedEndDate: cancelDate || null,
            };
            const res = await cancelSubscription(payload);
            if (res.status >= 200 && res.status < 300) {
                onNotify(
                    "Deine Kündigung wurde vorgemerkt. Wir haben dir eine Bestätigung per E-Mail gesendet.",
                    "success"
                );
                onClose();
                setCancelReason("");
                setCancelDate("");
                await onRefreshUser();
            } else {
                throw new Error("Kündigung konnte nicht verarbeitet werden.");
            }
        } catch (err) {
            logError("Fehler bei der Kündigung", err, { component: "Account" });
            const backendMessage = err?.response?.data;
            const message =
                typeof backendMessage === "string" && backendMessage.trim().length > 0
                    ? backendMessage
                    : "Kündigung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.";
            onNotify(message, "error");
        } finally {
            setCancelSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Abo kündigen</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {paidUntilDisplay !== "—"
                        ? `Dein aktueller Zugang ist bis zum ${paidUntilDisplay} aktiv. Du kannst ein abweichendes Kündigungsdatum wählen.`
                        : "Bitte bestätige, zu welchem Datum wir dein Abonnement beenden sollen."}
                </DialogContentText>
                <AppDatePicker
                    label="Kündigungsdatum"
                    value={cancelDate}
                    onChange={setCancelDate}
                    minDate={new Date(todayIso)}
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Begründung (optional)"
                    multiline
                    minRows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={cancelSubmitting}>
                    Abbrechen
                </Button>
                <Button onClick={handleCancelSubscription} color="error" variant="contained" disabled={cancelSubmitting}>
                    {cancelSubmitting ? <CircularProgress size={22} color="inherit" /> : "Kündigung senden"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
